/**
 * Interface display scale: a percentage (50..200 in steps of 5, 100 = the default density).
 *
 * It is applied as CSS custom properties on the root element, never as a global `zoom` or
 * `transform: scale()`:
 *
 * - `--ui-scale`      the user's setting as a number (1 = 100%);
 * - `--app-scale`     what the rest of the app follows, MODERATELY (30% of the deviation): the
 *                     root font size is `100% * --app-scale`, so headers, dialogs and forms
 *                     stay usable at 50% and 200%;
 * - `--kanban-scale`  what the Kanban board follows fully (see `app/styles/tokens.css`,
 *                     `[data-scale-scope='kanban']`), on top of a slightly more compact
 *                     baseline density.
 */
export const UI_SCALE_MIN = 50;
export const UI_SCALE_MAX = 200;
export const UI_SCALE_STEP = 5;
export const UI_SCALE_DEFAULT = 100;

/** Nearest valid value: clamped to the range and snapped to the step (NaN -> the default). */
export function normalizeScale(value: number): number {
  if (!Number.isFinite(value)) return UI_SCALE_DEFAULT;
  const snapped = Math.round(value / UI_SCALE_STEP) * UI_SCALE_STEP;
  return Math.min(UI_SCALE_MAX, Math.max(UI_SCALE_MIN, snapped));
}

export function isValidScale(value: number): boolean {
  return (
    Number.isInteger(value) &&
    value >= UI_SCALE_MIN &&
    value <= UI_SCALE_MAX &&
    value % UI_SCALE_STEP === 0
  );
}

/** The custom property set on the root for a percentage. */
export function scaleVars(percent: number): { '--ui-scale': string } {
  return { '--ui-scale': String(normalizeScale(percent) / 100) };
}

/** Immediate, side-effect-only application (the settings preview calls it while dragging). */
export function applyScale(percent: number, root: HTMLElement = document.documentElement): void {
  for (const [name, value] of Object.entries(scaleVars(percent))) {
    root.style.setProperty(name, value);
  }
}
